<template>
  <div class="users-list-tab">

    <!-- Header -->
    <div class="tab-header">
      <div class="tab-header-meta">
        <span class="tab-count">{{ accounts.length }} {{ accounts.length === 1 ? 'usuário' : 'usuários' }}</span>
      </div>
      <div v-if="!isAnalyst && !showAccountForm && !showInviteForm" class="header-actions">
        <button class="btn-invite" type="button" @click="showInviteForm = true">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1" y="2" width="12" height="9" rx="1.5" stroke="currentColor" stroke-width="1.4"/><path d="M1 4l6 4 6-4" stroke="currentColor" stroke-width="1.4"/></svg>
          Convidar
        </button>
        <button class="btn-new" type="button" @click="$emit('open-account-form')">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1v12M1 7h12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          Novo usuário
        </button>
      </div>
    </div>

    <!-- Invite form -->
    <div v-if="showInviteForm" class="form-panel">
      <div class="form-panel-header">
        <h3 class="form-panel-title">Convidar para o sistema</h3>
        <button class="btn-cancel-link" type="button" @click="closeInviteForm">Cancelar</button>
      </div>
      <p class="invite-desc">O usuário receberá um e-mail com um link para criar a própria conta.</p>

      <fieldset class="form-section">
        <legend class="form-section-label">E-mail</legend>
        <div class="field">
          <input class="field-input" v-model="inviteForm.email" type="email" placeholder="usuario@empresa.com" />
        </div>
      </fieldset>

      <fieldset class="form-section">
        <legend class="form-section-label">Nível de acesso</legend>
        <div class="access-cards">
          <label v-for="opt in accessOptions" :key="opt.value" class="access-card" :class="{ selected: inviteForm.role === opt.value }">
            <input type="radio" name="inv-access" :value="opt.value" v-model="inviteForm.role" />
            <span class="access-card-label">{{ opt.label }}</span>
          </label>
        </div>
      </fieldset>

      <fieldset class="form-section">
        <legend class="form-section-label">Cargo (opcional)</legend>
        <select class="field-input field-select" v-model="inviteForm.roleId">
          <option value="">Sem cargo</option>
          <option v-for="role in roles" :key="role.id" :value="role.id">{{ role.name }}</option>
        </select>
      </fieldset>

      <div v-if="inviteSuccess" class="invite-success">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="#22c55e" stroke-width="1.4"/><path d="M5 8l2 2 4-4" stroke="#22c55e" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
        Convite enviado para <strong>{{ inviteForm.email }}</strong>!
      </div>

      <div class="form-actions">
        <button class="btn-primary-action" type="button" :disabled="inviteLoading" @click="sendInvite">
          {{ inviteLoading ? 'Enviando…' : 'Enviar convite' }}
        </button>
        <button class="btn-ghost-action" type="button" @click="closeInviteForm">Cancelar</button>
      </div>
      <p v-if="inviteError" class="field-error form-error">{{ inviteError }}</p>
    </div>

    <!-- User list -->
    <template v-if="!showAccountForm">
      <SkeletonList v-if="showSkeleton" variant="row" :count="5" :columns="[10, 30, 20, 15]" />
      <div v-else-if="accounts.length === 0" class="empty-state">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none"><circle cx="20" cy="14" r="7" stroke="var(--ink-muted,#888)" stroke-width="2"/><path d="M6 36c0-7.732 6.268-14 14-14s14 6.268 14 14" stroke="var(--ink-muted,#888)" stroke-width="2" stroke-linecap="round"/></svg>
        <p>Nenhum usuário cadastrado.</p>
        <p class="empty-sub">Clique em "Novo usuário" para adicionar o primeiro membro.</p>
      </div>

      <div v-else class="user-cards">
        <div v-for="acc in accounts" :key="acc.username" class="user-card">
          <!-- Avatar -->
          <div class="user-avatar" :style="{ background: avatarColor(acc.name) }">
            <img v-if="acc.avatar" :src="$resolveUrl(acc.avatar)" alt="perfil" class="avatar-img" />
            <span v-else class="avatar-initials">{{ initials(acc.name) }}</span>
          </div>

          <!-- Info -->
          <div class="user-info">
            <div class="user-name">{{ acc.name }}</div>
            <div class="user-meta">
              <span class="user-username">@{{ acc.username }}</span>
              <span v-if="roleLabel(acc.roleId) !== 'Sem cargo'" class="role-chip">{{ roleLabel(acc.roleId) }}</span>
            </div>
          </div>

          <!-- Access badge -->
          <div class="user-access">
            <span class="access-badge" :class="'access-' + acc.role">{{ accessLabel(acc.role) }}</span>
          </div>

          <!-- Actions -->
          <div v-if="!isAnalyst" class="user-actions">
            <button class="action-btn" type="button" title="Editar" @click="$emit('edit-account', acc)">
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M10.5 2.5l2 2-8 8H2.5v-2l8-8z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>
            </button>
            <button class="action-btn action-btn--danger" type="button" title="Apagar" :disabled="acc.uid === currentUid" @click="$emit('delete-account', acc)">
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M3 4h9M6 4V2.5h3V4M5.5 4v7.5h4V4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- Form panel -->
    <div v-if="showAccountForm && !isAnalyst" class="form-panel">
      <div class="form-panel-header">
        <h3 class="form-panel-title">{{ accountForm.isEditing ? 'Editar usuário' : 'Novo usuário' }}</h3>
        <button class="btn-cancel-link" type="button" @click="$emit('close-account-form')">Cancelar</button>
      </div>

      <!-- Section: Identidade -->
      <fieldset class="form-section">
        <legend class="form-section-label">Identidade</legend>
        <div class="field-row">
          <div class="field">
            <label class="field-label" for="acc-name">Nome completo</label>
            <input id="acc-name" class="field-input" :value="accountForm.name" type="text" placeholder="Ex: João Silva" @input="updateField('name', ($event.target as HTMLInputElement).value)" />
          </div>
          <div class="field">
            <label class="field-label" for="acc-username">Username</label>
            <input id="acc-username" class="field-input" :value="accountForm.username" type="text" placeholder="joaosilva" :disabled="accountForm.isEditing" @input="updateField('username', ($event.target as HTMLInputElement).value)" />
          </div>
        </div>
        <div class="field-row">
          <div class="field">
            <label class="field-label" for="acc-email">E-mail <span class="field-hint">(recuperação de senha)</span></label>
            <input id="acc-email" class="field-input" :value="accountForm.email" type="email" placeholder="joao@empresa.com" @input="updateField('email', ($event.target as HTMLInputElement).value)" />
          </div>
          <div class="field">
            <label class="field-label" for="acc-birth">
              Data de nascimento
              <span class="field-hint">{{ accountForm.isEditing ? '(deixe em branco para manter)' : '(maior de 18 anos)' }}</span>
            </label>
            <input id="acc-birth" class="field-input" :value="accountForm.birthDate" type="date" :min="MIN_BIRTH_DATE" :max="maxBirthDate()" :required="!accountForm.isEditing" @input="updateField('birthDate', ($event.target as HTMLInputElement).value)" />
          </div>
        </div>
      </fieldset>

      <!-- Section: Acesso -->
      <fieldset class="form-section">
        <legend class="form-section-label">Acesso</legend>

        <div class="field-row">
          <div class="field">
            <label class="field-label" for="acc-role-id">Cargo</label>
            <select id="acc-role-id" class="field-input field-select" :value="accountForm.roleId" @change="updateField('roleId', ($event.target as HTMLSelectElement).value)">
              <option value="">Sem cargo</option>
              <option v-for="role in roles" :key="role.id" :value="role.id">{{ role.name }}</option>
            </select>
          </div>
          <div v-if="currentUserRole === 'admin-master'" class="field">
            <span class="field-label">Nível de acesso</span>
            <div class="access-cards">
              <label v-for="opt in accessOptions" :key="opt.value" class="access-card" :class="{ selected: accountForm.role === opt.value }">
                <input type="radio" name="acc-access" :value="opt.value" :checked="accountForm.role === opt.value" @change="updateField('role', opt.value)" />
                <span class="access-card-label">{{ opt.label }}</span>
              </label>
            </div>
          </div>
        </div>

        <!-- Password -->
        <div class="field">
          <div class="password-header">
            <label class="field-label" for="acc-password">
              {{ accountForm.isEditing ? 'Nova senha' : 'Senha' }}
            </label>
            <span v-if="accountForm.isEditing" class="field-hint">deixe em branco para manter a atual</span>
          </div>
          <div class="password-input-wrap">
            <input
              id="acc-password"
              class="field-input"
              :value="accountForm.password"
              :type="showPassword ? 'text' : 'password'"
              placeholder="mínimo 8 caracteres, 1 número"
              @input="updateField('password', ($event.target as HTMLInputElement).value)"
            />
            <button class="password-toggle" type="button" @click="showPassword = !showPassword">
              <svg v-if="!showPassword" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="currentColor" stroke-width="1.4"/><circle cx="8" cy="8" r="2" stroke="currentColor" stroke-width="1.4"/></svg>
              <svg v-else width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M1 1l14 14M6.5 6.7A2 2 0 0 0 9.3 9.5M3.7 3.9C2.2 5 1 8 1 8s2.5 5 7 5c1.4 0 2.7-.4 3.8-1M6 2.2C6.6 2.1 7.3 2 8 2c4.5 0 7 5 7 5s-.7 1.5-2 2.8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
            </button>
          </div>
          <p v-if="passwordError" class="field-error">{{ passwordError }}</p>
        </div>
      </fieldset>

      <!-- Section: Subordinados -->
      <fieldset class="form-section">
        <legend class="form-section-label">Subordinados</legend>
        <p class="section-hint">Usuários que este membro pode visualizar e gerenciar.</p>

        <div v-if="filledSubordinates.length > 0" class="sub-chips">
          <span v-for="username in filledSubordinates" :key="username" class="sub-chip">
            {{ subordinateLabel(username) }}
            <button type="button" class="sub-chip-remove" @click="removeSubordinate(username)">×</button>
          </span>
        </div>

        <div v-if="availableSubordinates.length > 0" class="sub-add-row">
          <select class="field-input field-select sub-add-select" @change="addSubordinate(($event.target as HTMLSelectElement).value); ($event.target as HTMLSelectElement).value = ''">
            <option value="">+ Adicionar subordinado</option>
            <option v-for="opt in availableSubordinates" :key="opt.username" :value="opt.username">
              {{ opt.name }} (@{{ opt.username }})
            </option>
          </select>
        </div>

        <p v-if="filledSubordinates.length === 0 && availableSubordinates.length === 0" class="empty-sub-msg">
          Nenhum usuário disponível para adicionar.
        </p>
      </fieldset>

      <!-- Actions -->
      <div class="form-actions">
        <button class="btn-primary-action" type="button" @click="handleSaveAccount">
          {{ accountForm.isEditing ? 'Salvar alterações' : 'Criar usuário' }}
        </button>
        <button class="btn-ghost-action" type="button" @click="$emit('close-account-form')">Cancelar</button>
      </div>
      <p v-if="accountError" class="field-error form-error">{{ accountError }}</p>
    </div>

  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, type PropType } from 'vue'
import { accountsStore, type Account } from '@/store/accounts'
import type { Role } from '@/store/roles'
import { apiStore } from '@/store/api'
import { maxBirthDate, MIN_BIRTH_DATE } from '@/utils/birthDate'
import SkeletonList from '@/components/skeleton/SkeletonList.vue'

interface AccountFormModel {
  uid: string
  name: string
  username: string
  password: string
  email: string
  birthDate: string
  role: 'admin-master' | 'admin-obra' | 'user'
  roleId: string
  subordinateUsernames: string[]
  isEditing: boolean
}

const AVATAR_COLORS = ['#2563eb', '#7c3aed', '#0891b2', '#059669', '#d97706', '#dc2626', '#db2777']

export default defineComponent({
  name: 'UsersListTab',
  components: { SkeletonList },
  props: {
    accounts: { type: Array as PropType<Account[]>, default: () => [] },
    currentUid: { type: String, default: '' },
    showAccountForm: { type: Boolean, default: false },
    accountForm: { type: Object as PropType<AccountFormModel>, required: true },
    accountError: { type: String, default: '' },
    roles: { type: Array as PropType<Role[]>, default: () => [] },
    currentUserRole: { type: String, default: 'user' },
    accountSubordinateOptions: { type: Array as PropType<Array<{ username: string; name: string }>>, default: () => [] },
    isAnalyst: { type: Boolean, default: false }
  },
  emits: [
    'refresh-accounts', 'open-account-form', 'edit-account', 'delete-account',
    'save-account', 'close-account-form', 'update-account-form',
    'add-account-subordinate', 'remove-account-subordinate'
  ],
  setup(props, { emit }) {
    const passwordError = ref('')
    const showPassword = ref(false)
    const showSkeleton = computed(() => accountsStore.state.loading && props.accounts.length === 0)

    // ── Invite form ──────────────────────────────────────────────────────────
    const showInviteForm = ref(false)
    const inviteForm = ref({ email: '', role: 'user', roleId: '' })
    const inviteLoading = ref(false)
    const inviteError = ref('')
    const inviteSuccess = ref(false)

    const closeInviteForm = () => {
      showInviteForm.value = false
      inviteForm.value = { email: '', role: 'user', roleId: '' }
      inviteError.value = ''
      inviteSuccess.value = false
    }

    const sendInvite = async () => {
      inviteError.value = ''
      inviteSuccess.value = false
      const email = inviteForm.value.email.trim()
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        inviteError.value = 'Informe um e-mail válido.'; return
      }
      inviteLoading.value = true
      try {
        const { sessionStore } = await import('@/store/session')
        const res = await fetch(`${apiStore.base}/invite-user`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionStore.token}` },
          body: JSON.stringify(inviteForm.value)
        })
        const data = await res.json()
        if (!res.ok) { inviteError.value = data.error || 'Erro ao enviar convite.'; inviteLoading.value = false; return }
        inviteSuccess.value = true
        inviteForm.value.email = ''
      } catch {
        inviteError.value = 'Erro de conexão. Tente novamente.'
      }
      inviteLoading.value = false
    }

    const accessOptions = [
      { value: 'user', label: 'Usuário' },
      { value: 'admin-obra', label: 'Admin' },
      { value: 'admin-master', label: 'Master' },
    ]

    const initials = (name: string) => {
      const parts = name.trim().split(/\s+/)
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      return name.slice(0, 2).toUpperCase()
    }

    const avatarColor = (name: string) => {
      const code = name.charCodeAt(0) || 0
      return AVATAR_COLORS[code % AVATAR_COLORS.length]
    }

    const roleLabel = (roleId: string) => {
      const role = props.roles.find((r) => r.id === roleId)
      return role ? role.name : roleId || 'Sem cargo'
    }

    const accessLabel = (role: string) => {
      if (role === 'admin-master') return 'Master'
      if (role === 'admin-obra') return 'Admin'
      return 'Usuário'
    }

    const subordinateLabel = (username: string) => {
      const opt = props.accountSubordinateOptions.find((o) => o.username === username)
      return opt ? `${opt.name}` : username
    }

    // Subordinate tag-picker helpers
    const filledSubordinates = computed(() =>
      props.accountForm.subordinateUsernames.filter((u) => u && u.trim() !== '')
    )

    const availableSubordinates = computed(() =>
      props.accountSubordinateOptions.filter(
        (opt) => !filledSubordinates.value.includes(opt.username)
      )
    )

    const addSubordinate = (username: string) => {
      if (!username) return
      // Find an empty slot or add new
      const idx = props.accountForm.subordinateUsernames.findIndex((u) => !u || u.trim() === '')
      if (idx !== -1) {
        emit('update-account-form', { field: 'subordinateUsernames', index: idx, value: username })
      } else {
        emit('add-account-subordinate')
        // After adding, set the new slot (it will be at the end)
        const newIdx = props.accountForm.subordinateUsernames.length
        emit('update-account-form', { field: 'subordinateUsernames', index: newIdx, value: username })
      }
    }

    const removeSubordinate = (username: string) => {
      const idx = props.accountForm.subordinateUsernames.indexOf(username)
      if (idx !== -1) {
        emit('remove-account-subordinate', idx)
      }
    }

    const updateField = (field: string, value: string) => {
      if (field === 'password') passwordError.value = ''
      emit('update-account-form', { field, value })
    }

    const handleSaveAccount = () => {
      const pwd = props.accountForm.password
      if (pwd) {
        if (pwd.length < 8) { passwordError.value = 'Senha deve ter pelo menos 8 caracteres.'; return }
        if (!/\d/.test(pwd)) { passwordError.value = 'Senha deve conter pelo menos um número.'; return }
      }
      passwordError.value = ''
      emit('save-account')
    }

    return {
      maxBirthDate, MIN_BIRTH_DATE,
      passwordError, showPassword, showSkeleton,
      accessOptions,
      initials, avatarColor, roleLabel, accessLabel, subordinateLabel,
      filledSubordinates, availableSubordinates,
      addSubordinate, removeSubordinate,
      updateField, handleSaveAccount,
      showInviteForm, inviteForm, inviteLoading, inviteError, inviteSuccess,
      closeInviteForm, sendInvite
    }
  }
})
</script>

<style scoped lang="scss">
.users-list-tab { display: flex; flex-direction: column; gap: 16px; }

/* ── Header ── */
.tab-header { display: flex; align-items: center; justify-content: space-between; }
.tab-count { font-size: 12px; color: var(--ink-muted, #888); font-weight: 500; }
.header-actions { display: flex; align-items: center; gap: 8px; }

.btn-invite {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 14px; border-radius: 6px; cursor: pointer;
  background: transparent;
  border: 1.5px solid var(--accent, #2563eb);
  color: var(--accent, #2563eb);
  font-size: 13px; font-weight: 600; transition: background .15s, color .15s;
  &:hover { background: var(--accent, #2563eb); color: #fff; }
  svg { flex-shrink: 0; }
}

.btn-new {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 14px; border-radius: 6px; border: none; cursor: pointer;
  background: var(--accent, #2563eb); color: #fff;
  font-size: 13px; font-weight: 600; transition: opacity .15s;
  &:hover { opacity: .88; }
  svg { flex-shrink: 0; }
}

.invite-desc {
  margin: -8px 0 4px;
  font-size: 13px;
  color: var(--ink-muted, #888);
}

.invite-success {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #15803d;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  padding: 10px 14px;
  svg { flex-shrink: 0; }
}

/* ── Empty ── */
.empty-state {
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  padding: 48px 24px; text-align: center; color: var(--ink-muted, #888);
  svg { opacity: .5; }
  p { margin: 0; font-size: 14px; font-weight: 500; color: var(--ink, #2c2b27); }
  .empty-sub { font-size: 12px; color: var(--ink-muted, #888); font-weight: 400; }
}

/* ── User cards ── */
.user-cards { display: flex; flex-direction: column; gap: 4px; }
.user-card {
  display: grid;
  grid-template-columns: 40px 1fr auto auto;
  align-items: center; gap: 12px;
  padding: 10px 12px; border-radius: 8px;
  transition: background .12s;
  &:hover { background: var(--surface, rgba(0,0,0,.03)); }
}

.user-avatar {
  width: 40px; height: 40px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; overflow: hidden;
}
.avatar-img { width: 100%; height: 100%; object-fit: cover; }
.avatar-initials { font-size: 13px; font-weight: 700; color: #fff; letter-spacing: .5px; }

.user-info { min-width: 0; }
.user-name { font-size: 14px; font-weight: 600; color: var(--ink, #2c2b27); }
.user-meta { display: flex; align-items: center; gap: 8px; margin-top: 2px; }
.user-username { font-size: 12px; color: var(--ink-muted, #888); }
.role-chip {
  font-size: 11px; font-weight: 500;
  padding: 1px 7px; border-radius: 10px;
  background: var(--surface, rgba(0,0,0,.06)); color: var(--ink-muted, #888);
}

.user-access { display: flex; align-items: center; }
.access-badge {
  font-size: 11px; font-weight: 600; padding: 2px 9px; border-radius: 10px;
  &.access-user { background: rgba(0,0,0,.06); color: var(--ink-muted, #666); }
  &.access-admin-obra { background: rgba(37,99,235,.12); color: #2563eb; }
  &.access-admin-master { background: rgba(124,58,237,.12); color: #7c3aed; }
}

.user-actions { display: flex; gap: 4px; align-items: center; }
.action-btn {
  display: flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border-radius: 6px; border: none;
  background: transparent; cursor: pointer; color: var(--ink-muted, #888);
  transition: background .12s, color .12s;
  &:hover { background: var(--surface, rgba(0,0,0,.06)); color: var(--ink, #2c2b27); }
  &.action-btn--danger:hover { background: rgba(220,38,38,.1); color: #dc2626; }
  &:disabled { opacity: .3; cursor: default; pointer-events: none; }
}

/* ── Form panel ── */
.form-panel {
  background: var(--card, #fff); border: 1px solid rgba(0,0,0,.08);
  border-radius: 12px; padding: 24px; display: flex; flex-direction: column; gap: 20px;
}
.form-panel-header { display: flex; align-items: center; justify-content: space-between; }
.form-panel-title { margin: 0; font-size: 16px; font-weight: 700; color: var(--ink, #2c2b27); }
.btn-cancel-link {
  background: none; border: none; cursor: pointer;
  font-size: 13px; color: var(--ink-muted, #888);
  padding: 4px; transition: color .12s;
  &:hover { color: var(--ink, #2c2b27); }
}

/* ── Form sections ── */
.form-section {
  border: none; padding: 0; margin: 0;
  display: flex; flex-direction: column; gap: 14px;
}
.form-section-label {
  font-size: 11px; font-weight: 700; letter-spacing: .6px;
  text-transform: uppercase; color: var(--ink-muted, #888);
  padding: 0; margin-bottom: 2px;
}
.section-hint { margin: -6px 0 0; font-size: 12px; color: var(--ink-muted, #888); }
.field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.field { display: flex; flex-direction: column; gap: 5px; }
.field-label { font-size: 12px; font-weight: 600; color: var(--ink, #2c2b27); }
.field-hint { font-size: 11px; color: var(--ink-muted, #888); font-weight: 400; }
.field-input {
  padding: 8px 10px; border-radius: 6px;
  border: 1px solid rgba(0,0,0,.14); background: var(--bg, #faf9f6);
  font-size: 13px; color: var(--ink, #2c2b27); outline: none;
  transition: border-color .15s;
  &:focus { border-color: var(--accent, #2563eb); }
  &:disabled { opacity: .5; cursor: default; }
}
.field-select { appearance: none; cursor: pointer; }
.field-error { font-size: 12px; color: #dc2626; margin: 0; }
.form-error { margin-top: -8px; }

/* Password */
.password-header { display: flex; align-items: baseline; gap: 8px; }
.password-input-wrap { position: relative; }
.password-input-wrap .field-input { width: 100%; box-sizing: border-box; padding-right: 38px; }
.password-toggle {
  position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
  background: none; border: none; cursor: pointer; color: var(--ink-muted, #888);
  display: flex; align-items: center;
  &:hover { color: var(--ink, #2c2b27); }
}

/* Access radio cards */
.access-cards { display: flex; gap: 8px; }
.access-card {
  display: flex; align-items: center; gap: 6px;
  padding: 6px 12px; border-radius: 6px; border: 1px solid rgba(0,0,0,.14);
  cursor: pointer; transition: border-color .15s, background .15s;
  input { display: none; }
  &.selected { border-color: var(--accent, #2563eb); background: rgba(37,99,235,.06); }
  &:hover:not(.selected) { border-color: rgba(0,0,0,.25); }
}
.access-card-label { font-size: 12px; font-weight: 600; color: var(--ink, #2c2b27); }

/* Subordinate picker */
.sub-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.sub-chip {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 10px 3px 10px; border-radius: 12px;
  background: rgba(37,99,235,.1); color: var(--accent, #2563eb);
  font-size: 12px; font-weight: 500;
}
.sub-chip-remove {
  background: none; border: none; cursor: pointer;
  font-size: 14px; line-height: 1; color: inherit; opacity: .7;
  padding: 0; display: flex; align-items: center;
  &:hover { opacity: 1; }
}
.sub-add-row { display: flex; }
.sub-add-select { max-width: 320px; cursor: pointer; }
.empty-sub-msg { font-size: 12px; color: var(--ink-muted, #888); margin: 0; }

/* ── Form actions ── */
.form-actions { display: flex; align-items: center; gap: 10px; padding-top: 4px; }
.btn-primary-action {
  padding: 9px 20px; border-radius: 7px; border: none; cursor: pointer;
  background: var(--accent, #2563eb); color: #fff;
  font-size: 13px; font-weight: 700; transition: opacity .15s;
  &:hover { opacity: .88; }
}
.btn-ghost-action {
  padding: 9px 16px; border-radius: 7px; cursor: pointer;
  background: transparent; border: 1px solid rgba(0,0,0,.14);
  font-size: 13px; font-weight: 600; color: var(--ink, #2c2b27);
  transition: background .12s;
  &:hover { background: var(--surface, rgba(0,0,0,.04)); }
}

@media (max-width: 600px) {
  .field-row { grid-template-columns: 1fr; }
  .form-actions { flex-wrap: wrap; }
}
</style>
